import React, { useMemo, useState, useEffect } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { Commodities, CommodityOptions } from "@/lib/api/commodity";
import { useListRiskSummaryFull } from "@/lib/api/risk/summary";
import { useDeleteManualClientPosition } from "@/lib/api/risk/manualEntries";
import { ClientPosition, useListRiskPositions } from "@/lib/api/risk/positions";
import { useListMarketZones } from "@/lib/api/generalContract";
import { useListRiskCashSales, RiskCashSale, RowFlagStatus, useActionCashSaleRows, ActionCashSales, RowFlagRule } from "@/lib/api/risk/cashSales/general";
import { get_customer_commodity_top_level, get_commodity_color, get_commodity_border_color, formatPercent, groupable_display_name_to_commodity_list, groupable_display_name_to_drillable_commodity_list, generateDisplayLabelForOtcDetailedType } from "./common";
import { ResponsivePie } from "@nivo/pie";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useQueryParam } from "@/hooks/use-query-param";
import { VisibilityState, RowSelectionState } from "@tanstack/react-table";
import { formatNumberToDisplay } from "@/lib/api/common";
import { dateSort, DataTable, type DataTableColumn } from "@/components/ui/DataTable/index";
import { ContractTypeOptions } from "@/lib/api/otc";
import { getPath } from "@/lib/prelude";
import { ColumnVisibilityDialog } from "@/components/ui/ColumnVisibilityDialog";
import { Button } from "@/components/ui/button";
import { PreviewDisplayCard } from "./PreviewDisplayCard";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { GenerateLoadingOrError } from "@/lib/utils";
import moment from "moment-timezone";
import { Badge } from "@/components/ui/badge";
import { ComboboxMulti } from "@/components/ui/combobox_mutli";
import { SelectOptionItem } from "@/lib/models/ui";
import { Loader, Plus, Edit, Trash2, MergeIcon, TriangleAlertIcon, ChevronDown, ChevronUp, SplitIcon, PlusIcon, DollarSign } from "lucide-react";
import { CropYearSelect } from "./CropYearSelect";
import { Checkbox } from "@/components/ui/checkbox";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useToast } from "@/components/ui/use-toast";
import { useQueryParamArray } from '@/hooks/use-query-param-array';
import { cn } from "@/lib/utils";
import { UpdateCashSalesDialog } from "./UpdateCashSalesDialog";
import { UpdateCashSalesFormDialog } from "./UpdateCashSalesFormDialog";
import { UploadCashReportDialog } from '../UploadCashReportDialog';
import { CreateManualEntryDialog } from "./CreateManualEntryDialog";
import { CreateManualCashSalesFormDialog } from "./CreateManualCashSalesFormDialog";
import { CreatePriceCashSalesFormDialog } from "./CreatePriceCashSalesFormDialog";
import { EditManualClientPositionDialog } from "./EditManualClientPositionDialog";
import { getPositionColumns } from "./positionDetailColumns";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { CommonDestinationTimezone } from "@/lib/utils";
import { RjoWorkingOrderUploadDialog } from "./RjoWorkingOrderUploadDialog";
import { generateCurrentDefaultCommodityYear } from "./dates";
import { AttachCustomerModal } from "./BrokerageDashboard/tt/components/AttachCustomerModal";

// Component to display cash sales that have flags
function FlaggedCashSales({ isExpanded, flagStatuses, onFlagStatusesChange }: { isExpanded: boolean; flagStatuses?: string[]; onFlagStatusesChange?: (statuses: string[]) => void }) {
    const { toast } = useToast();
    const [isHidingFullyPriced, setIsHidingFullyPriced] = useState(true);
    const [customerIds, _setCustomerIds] = useQueryParamArray("customer_ids");
    const [traderIds, _setTraderIds] = useQueryParamArray("trader_ids");
    const actionCashSaleRows = useActionCashSaleRows();
    const [createManualEntryDialogOpen, setCreateManualEntryDialogOpen] = useState(false);
    const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({
        created_at: false, // Hide the created_at column by default
        contract_release: false,
    });

    const [selectedFlagStatuses, setSelectedFlagStatuses] = useState<string[]>(flagStatuses || [
        "",
        RowFlagStatus.SurfaceAndExcludeFromStats,
        RowFlagStatus.SurfaceAndIncludeInStats
    ]);

    const [selectedCropYear, setSelectedCropYear] = useQueryParam("crop_year", generateCurrentDefaultCommodityYear());

    // Update when props change
    useEffect(() => {
        if (flagStatuses) {
            setSelectedFlagStatuses(flagStatuses);
        }
    }, [flagStatuses]);

    const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
    const [loadingRows, setLoadingRows] = useState<Record<string, 'create' | 'update' | 'delete'>>({});
    const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
    const [selectedRowForUpdate, setSelectedRowForUpdate] = useState<RiskCashSale | null>(null);
    const [updateFormDialogOpen, setUpdateFormDialogOpen] = useState(false);
    const [selectedRowForFormUpdate, setSelectedRowForFormUpdate] = useState<RiskCashSale | null>(null);
    const [createManualCashSalesDialogOpen, setCreateManualCashSalesDialogOpen] = useState(false);
    const [priceManualCashSalesDialogOpen, setPriceManualCashSalesDialogOpen] = useState(false);

    const [prepopulatedCashSaleData, setPrepopulatedCashSaleData] = useState<RiskCashSale | null>(null);
    
    const marketZonesResult = useListMarketZones();

    // Create options for the multi-select combobox
    const flagStatusOptions: SelectOptionItem<string>[] = [
        { value: "", label: "Valid Rows (Confirmed)" },
        { value: RowFlagStatus.SurfaceAndIncludeInStats, label: "Needs Review - Included in Stats" },
        { value: RowFlagStatus.SurfaceAndExcludeFromStats, label: "Needs Review - Excluded from Stats" },
        { value: RowFlagStatus.DoNotSurfaceOrShowInStats, label: "Ignored - Excluded from Stats" }
    ];

    const listCashSalesResult = useListRiskCashSales({
        filters: {
            customer_ids: customerIds.length > 0 ? customerIds : undefined,
            trader_ids: traderIds.length > 0 ? traderIds : undefined,
            flag_status: selectedFlagStatuses,
            is_enabled: true,
            surface_if_flagged_compound_id_appears: true,
        }
    });


    const { mappedCashSales } = useMemo(() => {
        const cashSales = listCashSalesResult?.data?.data?.data || [];
        const mappedCashSales = isHidingFullyPriced ? cashSales.filter((cashSale: typeof cashSales[0]) => {
            return cashSale.quantity !== "0";
        }) : cashSales;
        return { mappedCashSales };
    }, [listCashSalesResult?.data?.data?.data, isHidingFullyPriced])

    // Check if any of the rowIds are currently loading
    const hasOverlappingLoads = (rowIds: string[]) => {
        return rowIds.some(id => loadingRows[id]);
    };

    const handleCreate = async (rowIds: string[]) => {
        // Check for overlapping loads
        if (hasOverlappingLoads(rowIds)) {
            toast({
                title: "Action in progress",
                description: "Please wait for the current action to complete before starting another.",
                variant: "destructive"
            });
            return;
        }

        const updates: Record<string, 'create' | 'update' | 'delete'> = {};
        rowIds.forEach(id => {
            updates[id] = 'create';
        });
        setLoadingRows(prev => ({ ...prev, ...updates }));

        try {
            const result = await actionCashSaleRows({
                action: ActionCashSales.Create,
                data: {
                    ids: rowIds
                }
            });

            if (!result.success) {
                throw result;
            }

            // Clear selected rows on success
            setRowSelection({});
            toast({ title: "Created" });
        } catch (error) {
            console.error("Failed to action create cash sales", error);
            toast({
                title: "Failed to create cash sales",
                description: "An unexpected error occurred",
                variant: "destructive"
            });
        } finally {
            // Clear loading state
            setLoadingRows(prev => {
                const newState = { ...prev };
                rowIds.forEach(id => delete newState[id]);
                return newState;
            });
        }
    };

    const handleUpdate = async (rowIds: string[]) => {
        // Check for overlapping loads
        if (hasOverlappingLoads(rowIds)) {
            toast({
                title: "Action in progress",
                description: "Please wait for the current action to complete before starting another.",
                variant: "destructive"
            });
            return;
        }

        if (rowIds.length !== 1) {
            toast({
                title: "Invalid selection",
                description: "Please select exactly one row to update.",
                variant: "destructive"
            });
            return;
        }

        // Find the selected row
        const selectedRow = mappedCashSales.find((row: RiskCashSale) => row.id === rowIds[0]);
        if (!selectedRow) {
            toast({
                title: "Row not found",
                description: "Unable to find the selected row.",
                variant: "destructive"
            });
            return;
        }

        setSelectedRowForUpdate(selectedRow);
        setUpdateDialogOpen(true);
    };

    const handlePriceNewRow = (rowId: string) => {
         // Find the selected row
        const selectedRow = mappedCashSales.find((row: RiskCashSale) => row.id === rowId);
        if (!selectedRow) {
            toast({
                title: "Row not found",
                description: "Unable to find the selected row.",
                variant: "destructive"
            });
            return;
        }

        // Set the prepopulated data and open the dialog
        setPrepopulatedCashSaleData(selectedRow);
        setPriceManualCashSalesDialogOpen(true);
    };

    const handlePrepopulateWith = (rowId: string) => {
        // Find the selected row
        const selectedRow = mappedCashSales.find((row: RiskCashSale) => row.id === rowId);
        if (!selectedRow) {
            toast({
                title: "Row not found",
                description: "Unable to find the selected row.",
                variant: "destructive"
            });
            return;
        }

        // Set the prepopulated data and open the dialog
        setPrepopulatedCashSaleData(selectedRow);
        setCreateManualCashSalesDialogOpen(true);
    };

    const handleFormUpdate = async (rowId: string) => {
        // Find the selected row
        const selectedRow = mappedCashSales.find((row: RiskCashSale) => row.id === rowId);
        if (!selectedRow) {
            toast({
                title: "Row not found",
                description: "Unable to find the selected row.",
                variant: "destructive"
            });
            return;
        }

        setSelectedRowForFormUpdate(selectedRow);
        setUpdateFormDialogOpen(true);
    };

    const handleDelete = async (rowIds: string[]) => {
        // Check for overlapping loads
        if (hasOverlappingLoads(rowIds)) {
            toast({
                title: "Action in progress",
                description: "Please wait for the current action to complete before starting another.",
                variant: "destructive"
            });
            return;
        }

        const updates: Record<string, 'create' | 'update' | 'delete'> = {};
        rowIds.forEach(id => {
            updates[id] = 'delete';
        });
        setLoadingRows(prev => ({ ...prev, ...updates }));

        try {
            const result = await actionCashSaleRows({
                action: ActionCashSales.Delete,
                data: {
                    ids: rowIds
                }
            });

            if (!result.success) {
                throw result;
            }

            // Clear selected rows on success
            setRowSelection({});
            toast({ title: "Deleted" });
        } catch (error) {
            console.error("Failed to action delete cash sales", error);
            toast({
                title: "Failed to delete cash sales",
                description: error instanceof Error ? error.message : "An unexpected error occurred",
                variant: "destructive"
            });
        } finally {
            // Clear loading state
            setLoadingRows(prev => {
                const newState = { ...prev };
                rowIds.forEach(id => delete newState[id]);
                return newState;
            });
        }
    };

    const selectedRowCount = Object.keys(rowSelection).length;

    const handleCellClick = (row: RiskCashSale, cellId?: string) => {
        // Don't open edit dialog if clicking on select or actions columns
        if (cellId === 'select' || cellId === 'actions') return;
        
        setSelectedRowForFormUpdate(row);
        setUpdateFormDialogOpen(true);
    };

    const columns: DataTableColumn<RiskCashSale>[] = [
        {
            // actions have a forced width because they are react components and not text
            id: "actions",
            header: "Actions",
            minSize: 180,
            maxSize: 180,
            cell: ({ row }) => {
                const rowId = row.original.id;
                const loadingState = loadingRows[rowId];
                const isSelected = rowSelection[rowId];
                const isAnyLoading = !!loadingState;
                const isSplittable = !row.original.futures || !row.original.basis;

                return (
                    <div className="flex gap-1 flex-row items-center flex-nowrap">
                        {/* <Button
                            size="icon"
                            variant="outline"
                            onClick={() => handleCreate([rowId])}
                            disabled={isSelected || isAnyLoading || row.original.flag_status === null}
                            className="h-6 w-6"
                        >
                            {loadingState === 'create' ? <Loader className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                        </Button> */}
                        <Button
                            size="icon"
                            variant="outline"
                            onClick={() => handlePrepopulateWith(rowId)}
                            disabled={isSelected || isAnyLoading}
                            className="h-6 w-6"
                            title="Create new row based on"
                        >
                            {loadingState === 'create' ? <Loader className="h-4 w-4 animate-spin" /> : <PlusIcon className="h-4 w-4" />}
                        </Button>
                        <Button
                            size="icon"
                            variant="outline"
                            onClick={() => handlePriceNewRow(rowId)}
                            disabled={isSelected || isAnyLoading || !isSplittable}
                            className="h-6 w-6"
                            title="Price new row based on"
                        >
                            {loadingState === 'create' ? <Loader className="h-4 w-4 animate-spin" /> : <DollarSign className="h-4 w-4" />}
                        </Button>

                        <Button
                            size="icon"
                            variant="outline"
                            onClick={() => handleDelete([rowId])}
                            disabled={isSelected || isAnyLoading}
                            className="h-6 w-6"
                        >
                            {loadingState === 'delete' ? <Loader className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                        </Button>
                        {/* <Button
                            size="icon"
                            variant="outline"
                            onClick={() => handleUpdate([rowId])}
                            disabled={isSelected || isAnyLoading || row.original.flag_status === null || !row.original.appears_in_confirmed}
                            className="h-6 w-6"
                        >
                            {loadingState === 'update' ? <Loader className="h-4 w-4 animate-spin" /> : <MergeIcon className="h-4 w-4" />}
                        </Button> */}
                        <AppearsInConfirmedStatus flag_status={row.original.flag_status} appears_in_confirmed={row.original.appears_in_confirmed} />
                        <CashSaleAtAGlanceType row={row.original} />
                    </div>
                );
            },
            disableRowClick: true,
            enableSorting: false,
            enableFiltering: false,
        },
        {
            id: "customer_id",
            accessorKey: "customer_id",
            header: "Customer",
            accessorFn: (row: any) => row.Customer?.long_name || row.report_customer_id || "",
            cell: (info) => info.getValue() as string,
            /* cell: ({ row }) => row.original.Customer?.long_name || row.original.report_customer_id || "" */
        },
        {
            id: "commodity",
            accessorKey: "commodity",
            header: "Commodity",
            size: 80,
            cell: ({ row }) => row.original.commodity,
        },
        {
            id: "contract_number",
            accessorKey: "contract_number",
            header: "Contract Number",
            size: 80,
            cell: ({ row }) => row.original.contract_number,
        },
        {
            id: "option_month",
            accessorKey: "option_month",
            header: "Opt Month",
            size: 80,
            cell: ({ row }) => row.original.option_month,
        },
        {
            id: "crop_year",
            accessorKey: "crop_year",
            header: "Crop Year",
            aggregatedCell: () => '',
            size: 80,
            cell: ({ row }) => row.original.crop_year,
        },
        {
            id: "contract_type",
            accessorKey: "contract_type",
            header: "Contract type",
            size: 95,
            cell: (info) => {
            return (
                <span className="text-gray-600 text-sm">
                {generateDisplayLabelForOtcDetailedType(info.row.original.detailed_contract_type || info.row.original.contract_type)}
                </span>
            )
            },
        } as DataTableColumn<RiskCashSale>,
        {
            id: "quantity",
            accessorKey: "quantity",
            header: "Quantity",
            size: 80,
            cell: ({ row }) => formatNumberToDisplay(row.original.quantity),
        },
        {
            id: "report_market_zone_id",
            accessorKey: "report_market_zone_id",
            header: "Market Zone",
            size: 100,
            cell: ({ row }) => row.original.report_market_zone_id || row.original.MarketZone?.dtn_market_zone || "",
        },
        {
            id: "futures",
            accessorKey: "futures",
            header: "Futures",
            size: 80,
            cell: ({ row }) => {
                if (!row.original.futures) { return ''; }
                if (/cnl|canola/im.test(row.original.commodity || "")) {
                    return formatNumberToDisplay(row.original.futures, { minimumFractionDigits: 4, maximumFractionDigits: 4 });
                }
                return formatNumberToDisplay(row.original.futures, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

            },
        },
        {
            id: "basis",
            accessorKey: "basis",
            header: "Basis",
            size: 80,
            cell: ({ row }) => {
                if (!row.original.basis) { return ''; }
                if (/canola/im.test(row.original.commodity || "")) {
                    return formatNumberToDisplay(row.original.basis, { minimumFractionDigits: 4, maximumFractionDigits: 4 });
                }
                return formatNumberToDisplay(row.original.basis, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

            },
        },
        {
            id: "price",
            accessorKey: "price",
            header: "Price",
            size: 80,
            cell: ({ row }) => {
                if (!row.original.price) { return ''; }
                if (/canola/im.test(row.original.commodity || "")) {
                    return formatNumberToDisplay(row.original.price, { minimumFractionDigits: 4, maximumFractionDigits: 4 });
                }
                return formatNumberToDisplay(row.original.price, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

            },
        },
        {
            id: "note",
            accessorKey: "note",
            header: "Notes",
            size: 210,
            minSize: 100,
            enableTooltip: true,
            cell: ({ row }) => row.original.note,
        },
        {
            id: "start_date",
            accessorKey: "start_date",
            header: "Start Date",
            size: 110,
            accessorFn: (row: any) => moment.tz(row.start_date, CommonDestinationTimezone).format("MM/DD/YYYY"),
            cell: (info) => info.getValue() as string,
        },
        {
            id: "end_date",
            accessorKey: "end_date",
            header: "End Date",
            size: 110,
            accessorFn: (row: any) => moment.tz(row.end_date, CommonDestinationTimezone).format("MM/DD/YYYY"),
            cell: (info) => info.getValue() as string,
        },
        {
            id: "contract_release",
            accessorKey: "contract_release",
            header: "Contract Release",
            size: 80,
            cell: ({ row }) => row.original.contract_release,
        },
        {
            id: "flag_status",
            accessorKey: "flag_status",
            header: "Flag Status",
            cell: ({ row }) => convertToDisplayFlagStatus(row.original.flag_status),
        },
        {
            id: "status",
            accessorKey: "status",
            header: "Status",
            cell: ({ row }) => <span className="text-xs">{row.original.status}</span>,
        },
        {
            id: "scheduled_quantity",
            accessorKey: "scheduled_quantity",
            header: "Sched Qty",
            cell: ({ row }) => formatNumberToDisplay(row.original.balance),
        },
        {
            id: "balance",
            accessorKey: "balance",
            header: "Balance",
            cell: ({ row }) => formatNumberToDisplay(row.original.balance),
        },
        {
            id: "created_at",
            accessorKey: "created_at",
            header: "Created At",
            sortingFn: dateSort,
            accessorFn: (row: any) => moment.tz(row.created_at, CommonDestinationTimezone).format("MM/DD/YYYY HH:mm"),
            cell: (info) => info.getValue() as string,
        },
    ];


    const isLoading = listCashSalesResult?.isLoading;
    const isRefreshing = listCashSalesResult?.isFetching;
    const encounteredError = !isLoading && (!!listCashSalesResult?.error || !listCashSalesResult?.data?.success);

    const isOnlyConfirmedRows = selectedFlagStatuses.length === 1 && selectedFlagStatuses[0] === "";
    const isOnlyNeedsReviewRows = selectedFlagStatuses.length === 2 && selectedFlagStatuses.every(status => [RowFlagStatus.SurfaceAndExcludeFromStats,RowFlagStatus.SurfaceAndIncludeInStats].includes(status as RowFlagStatus));
    const isOnlyConfirmedAndReviewRows = selectedFlagStatuses.length === 3 && selectedFlagStatuses.every(status => ["", RowFlagStatus.SurfaceAndExcludeFromStats,RowFlagStatus.SurfaceAndIncludeInStats].includes(status as RowFlagStatus));
    const highlightedButtonClass = "border-yellow-400 bg-yellow-200";

    return (
        <div className="space-y-4 border border-thin border-1 p-2 rounded shadow-lg">

            {/* Bulk action buttons */}
            <div className="flex flex-col md:flex-row items-center gap-2 p-2 pl-2 bg-muted/50 rounded-md justify-between">
                <span className="flex items-center">
                    <span className="text-sm font-medium">{selectedRowCount} row{selectedRowCount !== 1 ? 's' : ''} selected</span>
                    <div className="flex gap-2 ml-4">
                        {/* <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                                const selectedIds = Object.keys(rowSelection);
                                handleCreate(selectedIds);
                            }}
                            disabled={selectedRowCount === 0 || hasOverlappingLoads(Object.keys(rowSelection))}
                        >
                            <Plus className="h-4 w-4 mr-1" />
                            Create
                        </Button> */}
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                                const selectedIds = Object.keys(rowSelection);
                                handleDelete(selectedIds);
                            }}
                            disabled={selectedRowCount === 0 || hasOverlappingLoads(Object.keys(rowSelection))}
                        >
                            <Trash2 className="h-4 w-4 mr-1" />
                            Delete
                        </Button>
                        {/* <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                                const selectedIds = Object.keys(rowSelection);
                                handleUpdate(selectedIds);
                            }}
                            disabled={selectedRowCount !== 1 || hasOverlappingLoads(Object.keys(rowSelection))}
                        >
                            <MergeIcon className="h-4 w-4 mr-1" />
                            Merge
                        </Button> */}
                    </div>
                </span>
                <span className="grid grid-cols-2 md:flex md:flex-row gap-2 items-end">
                    <Button
                        className={cn(isHidingFullyPriced ? highlightedButtonClass : "")}
                        size="sm"
                        variant="outline"
                        onClick={() => {
                            setIsHidingFullyPriced(!isHidingFullyPriced);
                        }}
                        disabled={isLoading}
                    >
                        Hiding Empty QTY
                    </Button>
                    {/* <Button
                        className={cn(isOnlyConfirmedAndReviewRows ? highlightedButtonClass : "")}
                        size="sm"
                        variant="outline"
                        onClick={() => {
                            const newStatuses = [
                                "",
                                RowFlagStatus.SurfaceAndExcludeFromStats,
                                RowFlagStatus.SurfaceAndIncludeInStats
                            ];
                            setSelectedFlagStatuses(newStatuses);
                            onFlagStatusesChange?.(newStatuses);
                        }}
                        disabled={isLoading}
                    >
                        Confirmed + Review
                    </Button>
                    <Button
                        className={cn(isOnlyConfirmedRows ? highlightedButtonClass : "")}
                        size="sm"
                        variant="outline"
                        onClick={() => {
                            const newStatuses = [""];
                            setSelectedFlagStatuses(newStatuses);
                            onFlagStatusesChange?.(newStatuses);
                        }}
                        disabled={isLoading}
                    >
                        Confirmed Rows
                    </Button>
                    <Button
                        className={cn(isOnlyNeedsReviewRows ? highlightedButtonClass : "")}
                        size="sm"
                        variant="outline"
                        onClick={() => {
                            const newStatuses = [
                                RowFlagStatus.SurfaceAndExcludeFromStats,
                                RowFlagStatus.SurfaceAndIncludeInStats
                            ];
                            setSelectedFlagStatuses(newStatuses);
                            onFlagStatusesChange?.(newStatuses);
                        }}
                        disabled={isLoading}
                    >
                        Needs Review
                    </Button> */}
                    <UploadCashReportDialog />

                    <Button
                        size="sm"
                        onClick={() => setCreateManualEntryDialogOpen(true)}
                        disabled={isLoading}
                    >
                        <Plus className="h-4 w-4 mr-1" />
                        Create Manual Entry
                    </Button>
                </span>
            </div>

            <DataTable
                tableId="ManageCashSalesCashSalesTables"
                persistColumnSettings
                className="shadow-lg"
                tableClassName={isExpanded ? "max-h-[calc(100vh-500px)] overflow-y-auto" : "max-h-[600px] overflow-y-auto"}
                data={mappedCashSales}
                columns={columns}
                stickyHeaders
                enableColumnVisibility
                enableSorting
                enableMultiSort
                enableFiltering
                enableGrouping
                enableColumnPinning
                virtualizationRowHeight={49}
                virtualizationOverscan={2}
                enableVirtualization
                enableColumnResizing
                enableExport
                filterStyle='select'
                filterMode='internal'
                emptyMessage={isLoading ? "Loading..." : "No cash sales with flags found"}
                defaultFiltering={(selectedCropYear && selectedCropYear !== 'all' && !isNaN(parseInt(selectedCropYear))) ? [{ id: "crop_year", value: [selectedCropYear] }] : undefined}
                defaultSorting={[
                    { id: "customer_id", desc: false }, { id: "commodity", desc: false },
                    { id: "contract_number", desc: true }, { id: "contract_release", desc: false },
                    { id: "created_at", desc: false }
                ]}
                rowClickable
                onRowClick={handleCellClick}
            />

            <UpdateCashSalesDialog
                isOpen={updateDialogOpen}
                onClose={() => setUpdateDialogOpen(false)}
                selectedRow={selectedRowForUpdate}
                onSuccess={() => {
                    setRowSelection({});
                }}
            />

            <UpdateCashSalesFormDialog
                isOpen={updateFormDialogOpen}
                onClose={() => setUpdateFormDialogOpen(false)}
                selectedRow={selectedRowForFormUpdate}
                onSuccess={() => {
                    setRowSelection({});
                }}
            />

            <CreateManualEntryDialog
                isOpen={createManualEntryDialogOpen}
                onClose={() => setCreateManualEntryDialogOpen(false)}
                onSuccess={() => {}}
                entryType="cash_sale"
            />

            <CreateManualCashSalesFormDialog
                isOpen={createManualCashSalesDialogOpen}
                onClose={() => {
                    setCreateManualCashSalesDialogOpen(false);
                    setPrepopulatedCashSaleData(null);
                }}
                onSuccess={() => {
                    setPrepopulatedCashSaleData(null);
                }}
                initialData={prepopulatedCashSaleData}
            />

            <CreatePriceCashSalesFormDialog
                isOpen={priceManualCashSalesDialogOpen}
                onClose={() => {
                    setPriceManualCashSalesDialogOpen(false);
                    setPrepopulatedCashSaleData(null);
                }}
                onSuccess={() => {
                    setPrepopulatedCashSaleData(null);
                }}
                initialData={prepopulatedCashSaleData}
                marketZones={marketZonesResult?.data?.data || []}
            />
        </div>
    );
}

function ClientPositions({ isExpanded }: { isExpanded: boolean; }) {
    const { toast } = useToast();
    const [enableDelayedQuery, setEnableDelayedQuery] = useState(false);
    const [showAllPositions, handleToggleShowAllBasePositions] = useState(false);
    const [customerIds, _setCustomerIds] = useQueryParamArray("customer_ids");
    const [traderIds, _setTraderIds] = useQueryParamArray("trader_ids");
    const [selectedView, setSelectedView] = useState<string>("All");
    const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({
        created_at: false
    });
    const [createManualEntryDialogOpen, setCreateManualEntryDialogOpen] = useState(false);
    const [defaultSourceType, setDefaultSourceType] = useState<string | undefined>(undefined);
    const [editPositionDialogOpen, setEditPositionDialogOpen] = useState(false);
    const [selectedPositionForEdit, setSelectedPositionForEdit] = useState<ClientPosition | null>(null);
    const [attachCustomerModalOpen, setAttachCustomerModalOpen] = useState(false);
    const [selectedPositionForAttach, setSelectedPositionForAttach] = useState<ClientPosition | null>(null);
    const [selectedCropYear, setSelectedCropYear] = useQueryParam("crop_year", moment.tz(CommonDestinationTimezone).format("YYYY"));

    // Update when prop changes

    const [loadingRows, setLoadingRows] = useState<Record<string, 'delete'>>({});
    const deleteManualClientPosition = useDeleteManualClientPosition();

    const positionsResult = useListRiskPositions({
        filters: {
            enabled: true,
            customer_ids: customerIds.length > 0 ? customerIds : undefined,
            trader_ids: traderIds.length > 0 ? traderIds : undefined,
        },
        enabled: enableDelayedQuery
    });

    useEffect(() => {
        const timer = setTimeout(() => {
            setEnableDelayedQuery(true);
        }, 1000);
        return () => clearTimeout(timer);
    }, []);

    const wrapperData = positionsResult?.data?.data || {};
    const applyHedgeableFilter = (row: any) => (showAllPositions ? true : typeof row.is_hedgeable === 'boolean' ? row.is_hedgeable : false);
    const wrapperDataPositions = wrapperData?.positions?.filter(applyHedgeableFilter);
    const wrapperManualOpenDataPositions = wrapperData.open_offers?.filter(applyHedgeableFilter);


    useEffect(() => {
        switch (selectedView) {
            case "All":
                setColumnVisibility({ created_at: false, long_qty: true, short_qty: true });
                break;
            case "RJO":
                setColumnVisibility({ created_at: false, long_qty: true, short_qty: true });
                break;
            case "Manual Open Offers":
                setColumnVisibility({ created_at: false, long_qty: false, short_qty: false });
                break;
            case "Manual OTC":
                setColumnVisibility({ created_at: false, long_qty: false, short_qty: false });
                break;
            case "OTC Offer Ups":
                setColumnVisibility({ created_at: false, long_qty: false, short_qty: false });
                break;
        }
    }, [selectedView])

    const { rows } = useMemo(() => {
        const rows = [];

        if (selectedView === 'All' || selectedView === 'RJO') { rows.push(...(wrapperDataPositions || [])); }
        if (selectedView === 'All' || selectedView === 'Manual Open Offers') { rows.push(...(wrapperManualOpenDataPositions || [])); }
        if (selectedView === 'All' || selectedView === 'Manual OTC') { rows.push(...(wrapperData.otc_offer_ups?.filter(applyHedgeableFilter) || [])); }
        if (selectedView === 'All' || selectedView === 'Manual OTC') { rows.push(...(wrapperData.pos_reown_contracts?.filter(applyHedgeableFilter) || [])); }
        if (selectedView === 'All' || selectedView === 'OTC Offer Ups') { rows.push(...(wrapperData.inferred_otc_offer_ups || [])); }


        return { rows };
    }, [wrapperData, selectedView, showAllPositions])

    const handleDelete = async (rowIds: string[]) => {
        if (rowIds.length > 1) {
            return null;
        }

        const updates: Record<string, 'delete'> = {};
        rowIds.forEach(id => { updates[id] = 'delete'; });
        setLoadingRows(prev => ({ ...prev, ...updates }));

        try {
            const result = await deleteManualClientPosition(rowIds[0]);

            if (!result.success) {
                throw result;
            }

            toast({ title: "Deleted" });
        } catch (error) {
            console.error("Failed to action delete position", error);
            toast({
                title: "Failed to delete position",
                description: error instanceof Error ? error.message : "An unexpected error occurred",
                variant: "destructive"
            });
        } finally {
            // Clear loading state
            setLoadingRows(prev => {
                const newState = { ...prev };
                rowIds.forEach(id => delete newState[id]);
                return newState;
            });
        }
    };

    const handlePositionRowClick = (position: ClientPosition) => {
        // Check if this is a TT open order
        if (position.is_tt_open_order) {
            setSelectedPositionForAttach(position);
            setAttachCustomerModalOpen(true);
            return;
        }
        
        // Check if this is a manually inserted position
        // A position is manual if:
        // 1. is_manually_inserted is explicitly true, OR
        // 2. source_type is not "rjo" and not "Open Orders"
        const isManual = position.is_manually_inserted === true;
        
        if (isManual) {
            setSelectedPositionForEdit(position);
            setEditPositionDialogOpen(true);
        } else {
            toast({
                title: "Cannot edit position",
                description: "Only manual positions can be edited.",
                variant: "destructive"
            });
        }
    };

    const columns = getPositionColumns({ disableActions: selectedView === "OTC Offer Ups", loadingRows, handleDelete });

    const isLoading = positionsResult?.isLoading;
    const isRefreshing = positionsResult?.isFetching;
    const isHighlighted = (status: "All" | "RJO" | "Manual Open Offers" | "Manual OTC" | "OTC Offer Ups") => selectedView === status;
    const encounteredError = !isLoading && (!!positionsResult?.error || !positionsResult?.data?.success);
    const highlightedButtonClass = "border-yellow-400 bg-yellow-200";

    const totaledCount = (rows?.length || 0);

    return (
        <div className="flex flex-col max-w-full space-y-4 border border-thin border-1 p-2 rounded shadow-lg">

            {/* Bulk action buttons */}
            <div className="flex flex-col md:flex-row items-center gap-2 p-2 pl-2 bg-muted/50 rounded-md justify-between">
                <span>{positionsResult && !positionsResult.isLoading ? <>({totaledCount} rows)</> : '...'}</span>
                <span className="grid grid-cols-2 md:flex md:flex-row gap-2 items-end">
                    {(isHighlighted("All") || isHighlighted("RJO")) && (
                        <span className="flex flex-row gap-2">
                            <Label className="text-sm font-medium text-muted-foreground">Hedgeable Only</Label>
                            <Switch
                                checked={!showAllPositions}
                                onCheckedChange={() => handleToggleShowAllBasePositions(!showAllPositions)}
                            />
                        </span>
                    )}
                    <Button
                        className={cn(isHighlighted("All") ? highlightedButtonClass : "")}
                        size="sm"
                        variant="outline"
                        onClick={() => { setSelectedView("All")}}
                        disabled={isLoading}
                    >
                        All
                    </Button>
                    <Button
                        className={cn(isHighlighted("RJO") ? highlightedButtonClass : "")}
                        size="sm"
                        variant="outline"
                        onClick={() => { setSelectedView("RJO")}}
                        disabled={isLoading}
                    >
                        RJO
                    </Button>
                    <Button
                        className={cn(isHighlighted("Manual Open Offers") ? highlightedButtonClass : "")}
                        size="sm"
                        variant="outline"
                        onClick={() => { setSelectedView("Manual Open Offers")}}
                        disabled={isLoading}
                    >
                        Manual Open Offers
                    </Button>
                    <Button
                        className={cn(isHighlighted("Manual OTC") ? highlightedButtonClass : "")}
                        size="sm"
                        variant="outline"
                        onClick={() => { setSelectedView("Manual OTC")}}
                        disabled={isLoading}
                    >
                        Manual OTC
                    </Button>
                    <Button
                        className={cn(isHighlighted("OTC Offer Ups") ? highlightedButtonClass : "")}
                        size="sm"
                        variant="outline"
                        onClick={() => { setSelectedView("OTC Offer Ups")}}
                        disabled={isLoading}
                    >
                        OTC Offer Ups
                    </Button>


                    <Button
                        size="sm"
                        onClick={() => {
                            setDefaultSourceType(undefined);
                            setCreateManualEntryDialogOpen(true);
                        }}
                        disabled={isLoading}
                    >
                        <Plus className="h-4 w-4 mr-1" />
                        Create Open Offer
                    </Button>
                    <Button
                        size="sm"
                        onClick={() => {
                            setDefaultSourceType("RJO");
                            setCreateManualEntryDialogOpen(true);
                        }}
                        disabled={isLoading}
                    >
                        <Plus className="h-4 w-4 mr-1" />
                        Create Brokerage Entry
                    </Button>
                    <RjoWorkingOrderUploadDialog />
                </span>
            </div>

            <DataTable
                tableId="ManageCashSalesPositionTables"
                persistColumnSettings
                defaultSorting={[
                    { id: "customer_id", desc: false }, { id: "commodity", desc: false },
                    { id: "contract_number", desc: true }, { id: "contract_release", desc: false },
                    { id: "created_at", desc: false }
                ]}
                columns={columns as any}
                data={rows}
                stickyHeaders
                enableColumnVisibility
                enableSorting
                enableMultiSort
                enableFiltering
                enableGrouping
                enableVirtualization
                enableColumnResizing
                enableColumnPinning
                virtualizationRowHeight={49}
                virtualizationOverscan={2}
                enableExport
                filterStyle='select'
                filterMode='internal'
                className="shadow-lg"
                tableClassName={isExpanded ? "max-h-[calc(100vh-500px)] overflow-y-auto" : "max-h-[600px] overflow-y-auto"}
                emptyMessage={isLoading ? "Loading..." : "No positions found"}
                rowClickable
                onRowClick={handlePositionRowClick}
            />

            <CreateManualEntryDialog
                isOpen={createManualEntryDialogOpen}
                onClose={() => setCreateManualEntryDialogOpen(false)}
                onSuccess={() => {}}
                entryType="client_position"
                defaultSourceType={defaultSourceType}
            />

            <EditManualClientPositionDialog
                isOpen={editPositionDialogOpen}
                onClose={() => {
                    setEditPositionDialogOpen(false);
                    setSelectedPositionForEdit(null);
                }}
                onSuccess={() => {
                    setSelectedPositionForEdit(null);
                }}
                position={selectedPositionForEdit}
            />

            <AttachCustomerModal
                open={attachCustomerModalOpen}
                onOpenChange={setAttachCustomerModalOpen}
                guidOrderId={selectedPositionForAttach?.existingTtAssociatedMetaData?.guid_order_id || ''}
                existingMetadata={selectedPositionForAttach?.existingTtAssociatedMetaData}
                orderTextFields={{
                    textA: selectedPositionForAttach?.text_a,
                    textB: selectedPositionForAttach?.text_b,
                    textC: selectedPositionForAttach?.text_c,
                    textTT: selectedPositionForAttach?.text_tt,
                }}
            />
        </div>
    );
}

export function ManageCashSales({}: {}) {
    const [expandedSection, setExpandedSection] = useState<'contract' | 'positions' | null>('contract');
    const [customerIds] = useQueryParamArray("customer_ids");
    const [traderIds] = useQueryParamArray("trader_ids");
    const [selectedFlagStatuses, setSelectedFlagStatuses] = useState<string[]>([
        "",
        RowFlagStatus.SurfaceAndExcludeFromStats,
        RowFlagStatus.SurfaceAndIncludeInStats
    ]);

    // Get data for row counts in collapsed headers
    const listCashSalesResult = useListRiskCashSales({
        filters: {
            customer_ids: customerIds.length > 0 ? customerIds : undefined,
            trader_ids: traderIds.length > 0 ? traderIds : undefined,
            flag_status: selectedFlagStatuses,
            is_enabled: true,
            surface_if_flagged_compound_id_appears: true,
        }
    });
    
    const positionsResult = useListRiskPositions({
        filters: {
            enabled: true,
            customer_ids: customerIds.length > 0 ? customerIds : undefined,
            trader_ids: traderIds.length > 0 ? traderIds : undefined,
        }
    });

    return (
        <div className="flex flex-col gap-4">
            <div className="space-y-2">
                <div className="border border-thin border-1 p-2 rounded shadow-lg">
                    <div className="flex flex-col md:flex-row items-center justify-between">
                        <Button
                            variant="ghost"
                            className="flex-1 justify-between p-2 text-left hover:bg-muted/50"
                            onClick={() => setExpandedSection(expandedSection === 'contract' ? 'positions' : 'contract')}
                        >
                            <div className="flex flex-col md:flex-row items-center gap-4">
                                <span className="text-lg font-semibold">Contract Release <span className="font-medium text-sm text-muted-foreground ml-2">{listCashSalesResult && !listCashSalesResult.isLoading ? <>({listCashSalesResult?.data?.data?.data?.length || 0} rows)</> : '...'}</span></span>
                                {expandedSection === 'contract' && (
                                    <span className="flex flex-col md:flex-row items-center gap-2">
                                        {!listCashSalesResult.isLoading && !!listCashSalesResult.error && (<span className="text-red-500 text-sm">Error loading data</span>)}
                                        {!listCashSalesResult.isLoading && listCashSalesResult.isFetching && (<span className="text-muted-foreground text-sm flex items-center">Refreshing... <Loader className="w-3 h-3 animate-spin ml-1" /></span>)}
                                        {(!listCashSalesResult.isLoading && !listCashSalesResult.isFetching && !listCashSalesResult.error) ? (
                                            <span className="text-muted-foreground text-sm">Data Loaded</span>
                                        ) : null}
                                    </span>
                                )}
                            </div>
                            {expandedSection === 'contract' ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                        </Button>
                        {/* {expandedSection === 'contract' && (
                            <div className="flex gap-2 items-center px-2 md:min-w-[600px] flex-end">
                                <ComboboxMulti
                                    options={[
                                        { value: "", label: "Valid Rows (Confirmed)" },
                                        { value: RowFlagStatus.SurfaceAndIncludeInStats, label: "Needs Review - Included in Stats" },
                                        { value: RowFlagStatus.SurfaceAndExcludeFromStats, label: "Needs Review - Excluded from Stats" },
                                        { value: RowFlagStatus.DoNotSurfaceOrShowInStats, label: "Ignored - Excluded from Stats" }
                                    ]}
                                    value={selectedFlagStatuses}
                                    onValueChange={setSelectedFlagStatuses}
                                    placeholder="Select flag statuses..."
                                    className="max-w-[600px] min-w-[300px] lg:w-full"
                                    popoverClassName="max-w-[400px] min-w-[300px]"
                                />
                            </div>
                        )} */}
                    </div>
                </div>
                <div className={cn(
                    "flex max-w-full transition-all duration-300 ease-in-out overflow-y-hidden",
                    expandedSection === 'contract' ? " flex flex-col max-w-full max-h-[1200px] opacity-100" : "max-h-0 opacity-0"
                )}>
                    <FlaggedCashSales
                        isExpanded={expandedSection === 'contract'}
                        flagStatuses={selectedFlagStatuses}
                        onFlagStatusesChange={setSelectedFlagStatuses}
                    />
                </div>
            </div>

            <div className="space-y-2">
                <div className="border border-thin border-1 p-2 rounded shadow-lg">
                    <div className="flex items-center justify-between">
                        <Button
                            variant="ghost"
                            className="flex-1 justify-between p-2 text-left hover:bg-muted/50"
                            onClick={() => setExpandedSection(expandedSection === 'positions' ? 'contract' : 'positions')}
                        >
                            <div className="flex items-center gap-4">
                                <span className="text-lg font-semibold">Positions <span className="font-medium text-sm text-muted-foreground ml-2"></span></span>
                                {expandedSection === 'positions' && (
                                    <span className="flex flex-row items-center gap-2">
                                        {!positionsResult.isLoading && !!positionsResult.error && (<span className="text-red-500 text-sm">Error loading data</span>)}
                                        {!positionsResult.isLoading && positionsResult.isFetching && (<span className="text-muted-foreground text-sm flex items-center">Refreshing... <Loader className="w-3 h-3 animate-spin ml-1" /></span>)}
                                        {(!positionsResult.isLoading && !positionsResult.isFetching && !positionsResult.error) ? (
                                            <span className="text-muted-foreground text-sm">Data Loaded</span>
                                        ) : null}
                                    </span>
                                )}
                            </div>
                            {expandedSection === 'positions' ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                        </Button>
                    </div>
                </div>
                <div className={cn(
                    "flex max-w-full transition-all duration-300 ease-in-out overflow-y-hidden",
                    expandedSection === 'positions' ? " flex flex-col max-w-full max-h-[1200px] opacity-100" : "max-h-0 opacity-0"
                )}>
                    <ClientPositions isExpanded={expandedSection === 'positions'} />
                </div>
            </div>
        </div>
    );
}

function convertToDisplayFlagStatus(flag_status?: RowFlagStatus) {
    if (!flag_status) return <span className="text-green-600">Valid (Confirmed)</span>;
    switch (flag_status) {
        case RowFlagStatus.DoNotSurfaceOrShowInStats:
            return <span className="text-red-600">Ignored</span>;
        case RowFlagStatus.SurfaceAndExcludeFromStats:
            return <span className="text-yellow-600">Exclude from Stats</span>;
        case RowFlagStatus.SurfaceAndIncludeInStats:
            return <span className="text-yellow-600">Include in Stats</span>;
    }
}

function AppearsInConfirmedStatus({ flag_status, appears_in_confirmed }: { flag_status?: string | null; appears_in_confirmed?: boolean; }) {
    if (!flag_status) { return <span className="h-4 w-4"></span>; }
    if (appears_in_confirmed) {
        return (
            <Tooltip>
                <TooltipTrigger><TriangleAlertIcon className="h-4 w-4 text-orange-400" /></TooltipTrigger>
                <TooltipContent>
                    <p>Already appears as a confirmed row</p>
                </TooltipContent>
            </Tooltip>
        )
    }

    return (
        <Tooltip>
            <TooltipTrigger><TriangleAlertIcon className="h-4 w-4 text-green-400" /></TooltipTrigger>
            <TooltipContent>
                <p>Does not appear as a confirmed row</p>
            </TooltipContent>
        </Tooltip>
    )

}

function CashSaleAtAGlanceType({ row }: { row: RiskCashSale }) {
    if (row.futures && row.basis) {
        return (
            <Tooltip>
                <TooltipTrigger><span className="h-4 w-4">CS</span></TooltipTrigger>
                <TooltipContent>
                    <p>Cash Sale</p>
                </TooltipContent>
            </Tooltip>
        )
    }

    if (!row.futures && !!row.basis) {
        return (
            <Tooltip>
                <TooltipTrigger><span className="h-4 w-4">P</span></TooltipTrigger>
                <TooltipContent>
                    <p>Physical</p>
                </TooltipContent>
            </Tooltip>
        )
    }

    if (!!row.futures && !row.basis) {
        return (
            <Tooltip>
                <TooltipTrigger><span className="h-4 w-4">F</span></TooltipTrigger>
                <TooltipContent>
                    <p>Futures</p>
                </TooltipContent>
            </Tooltip>
        )
    }

    return (
        <Tooltip>
            <TooltipTrigger>O</TooltipTrigger>
            <TooltipContent>
                <p>Other</p>
            </TooltipContent>
        </Tooltip>
    )

}
